import cn from "classnames";
import { ResponsivePie } from "@nivo/pie";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import { getCostChartConfig } from "./CostChart.config";

import { convertValueToLocale } from "helpers/convertValueToLocale";

import { InvestmentGraphic } from "components/InvestmentGraphic";

import { Typography } from "shared/ui/components/Typography";

import styles from "./CostChart.module.scss";

const CustomTooltip = ({ datum }: { datum: any }) => (
  <div className={styles.tooltip}>
    <Typography className={styles.tooltipText} variant="bodyL">
      {convertValueToLocale(datum.value)}
    </Typography>
  </div>
);

const _renderDescription = (costsChart: any, t: TFunction) => {
  if (costsChart.diffTotalCosts > 0) {
    return (
      <div className={styles.description}>
        <Typography variant="bodyXS" className={styles.altCosts}>
          {convertValueToLocale(costsChart.altTotalCosts)}
        </Typography>
        <Typography variant="bodyXS">
          {t("calculator_pro_page.costs_chart.description", {
            price: convertValueToLocale(costsChart.diffTotalCosts),
          })}
        </Typography>
        <Typography variant="bodyXS" weight="semiBold">
          {convertValueToLocale(costsChart.diffTotalCosts)}
        </Typography>
      </div>
    );
  }
};

const _renderInfoPopover = ({ t, info }: { t: TFunction; info: any }) => {
  return (
    <div className={styles.popover}>
      {!!info?.discountRate && (
        <Typography variant="bodyS" weight="light">
          {t("calculator_pro_page.costs_chart.info_popover_title", {
            discountRate: convertValueToLocale(info?.discountRate, 2, "%") || 0,
          })}
        </Typography>
      )}
      <div className={styles.popoverInfoItem}>
        <Typography
          variant="bodyS"
          weight="semiBold"
          className={styles.popoverInfoItemTitle}
        >
          {t(
            `calculator_pro_page.costs_chart.info.apt_info_popover_header_description`
          )}
        </Typography>
        {!!info?.discountRate && (
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {t(
              "calculator_pro_page.costs_chart.info.info_popover_header_no_discount"
            )}
          </Typography>
        )}
        {!!info?.discountRate && (
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {t(
              "calculator_pro_page.costs_chart.info.info_popover_header_discount"
            )}
          </Typography>
        )}
      </div>

      <div className={styles.separator} />

      {info?.aptsPrices?.map((item: any, index) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(`calculator_pro_page.costs_chart.info.apt_title`)}
          </Typography>

          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {convertValueToLocale(item.price)}
          </Typography>
          {!!info?.discountRate && (
            <Typography
              variant="bodyS"
              weight="light"
              className={styles.popoverInfoItemValue}
            >
              {convertValueToLocale(item.discountedPrice)}
            </Typography>
          )}
        </div>
      ))}
      {info?.parkingsPrices?.map((item: any, index) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(`calculator_pro_page.costs_chart.info.parking_title`)}
          </Typography>

          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {convertValueToLocale(item.price)}
          </Typography>
          {!!info?.discountRate && (
            <Typography
              variant="bodyS"
              weight="light"
              className={styles.popoverInfoItemValue}
            >
              {convertValueToLocale(item.discountedPrice)}
            </Typography>
          )}
        </div>
      ))}
      {info?.furniturePrices?.map((item: any, index) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(`calculator_pro_page.costs_chart.info.furniture_title`)}
          </Typography>

          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {convertValueToLocale(item.price)}
          </Typography>
          {!!info?.discountRate && (
            <Typography
              variant="bodyS"
              weight="light"
              className={styles.popoverInfoItemValue}
            >
              {convertValueToLocale(item.discountedPrice)}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
};

const Legend = ({
  t,
  chartConfig,
  costsChart,
}: {
  t: TFunction;
  chartConfig: any;
  costsChart: any;
}) => (
  <div className={styles.legend}>
    {chartConfig.map((item: any) => {
      if (item.id === "investObjectPriceWithFurniture") {
        const aquisitionsPrice = costsChart.totalCosts - item.value;
        const aquisitionsRate = chartConfig.reduce((acc: number, item: any) => {
          return acc + (item.rate ?? 0);
        }, 0);

        const buildingRate = 100 - aquisitionsRate;

        return (
          <div key={item.id}>
            <div className={cn(styles.legendItem, styles.legendItemTop)}>
              <Typography
                variant="bodyS"
                weight="semiBold"
                className={styles.legendItemLabel}
              >
                {t(`calculator_pro_page.costs_chart.${item.id}`)}
              </Typography>
              <Typography
                data-testid={`${item.id}`}
                variant="numbers"
                weight="semiBold"
                className={styles.legendItemPrice}
              >
                {convertValueToLocale(item.value)}
              </Typography>
              {/* <Typography
                data-testid={`${item.id}Percent`}
                variant="numbers"
                weight="semiBold"
                className={cn(styles.legendItemPercent, {
                  [styles.legendItemPercentHidden]: buildingRate === null,
                })}
              >
                {convertValueToLocale(buildingRate, 1, "%")}
              </Typography> */}
            </div>
            <div className={cn(styles.legendItem, styles.legendItemTop)}>
              <Typography
                variant="bodyS"
                weight="semiBold"
                className={styles.legendItemLabel}
              >
                {t(`calculator_pro_page.costs_chart.net_costs`)}
              </Typography>
              <Typography
                data-testid={"acquisitionsPrice"}
                variant="numbers"
                weight="semiBold"
                className={styles.legendItemPrice}
              >
                {convertValueToLocale(aquisitionsPrice)}
              </Typography>
              {/* <Typography
                data-testid={"acquisitionsPricePercent"}
                variant="numbers"
                weight="semiBold"
                className={cn(styles.legendItemPercent, {
                  [styles.legendItemPercentHidden]: aquisitionsRate === null,
                })}
              >
                {convertValueToLocale(aquisitionsRate, 1, "%")}
              </Typography> */}
            </div>
          </div>
        );
      }

      return (
        <div className={styles.legendItem} key={item.id}>
          <div
            className={styles.legendItemIcon}
            style={{ backgroundColor: item.color }}
          />
          <Typography variant="bodyS" className={styles.legendItemLabel}>
            {t(`calculator_pro_page.costs_chart.${item.id}`)}
          </Typography>
          <Typography
            data-testid={`${item.id}`}
            variant="numbers"
            weight="regular"
            className={styles.legendItemPrice}
          >
            {convertValueToLocale(item.value)}
          </Typography>
          {/* <Typography
            data-testid={`${item.id}Percent`}
            variant="numbers"
            className={cn(styles.legendItemPercent, {
              [styles.legendItemPercentHidden]: item.rate === null,
            })}
            weight="regular"
          >
            {convertValueToLocale(item.rate, 1, "%")}
          </Typography> */}
        </div>
      );
    })}
  </div>
);

const CenteredMetric = ({ totalCosts, centerX, centerY }) => {
  return (
    <g>
      <text
        x={centerX}
        y={centerY}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: 18,
          lineHeight: 24,
          fontFamily: "Reddit Sans",
          fontWeight: 400,
        }}
      >
        {convertValueToLocale(totalCosts)}
      </text>
    </g>
  );
};

const CostChart = ({ costsChart }: { costsChart: any }) => {
  const { t } = useTranslation();

  const chartConfig = getCostChartConfig(costsChart);

  return (
    <div className={styles.costChartWrapper}>
      <InvestmentGraphic
        className={styles.chart}
        bigGraphic={true}
        positive
        description={
          costsChart.diffTotalCosts > 0 && _renderDescription(costsChart, t)
        }
        // infoPopover={_renderInfoPopover({ t, info: costsChart.info })}
      >
        <div className={styles.header}>
          <Typography className={styles.title} variant="h5" weight="semiBold">
            {t("calculator_pro_page.costs_chart.title")}
          </Typography>
        </div>

        <div className={styles.inner}>
          <div className={styles.pie}>
            <ResponsivePie
              data={chartConfig}
              colors={{ datum: "data.color" }}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              innerRadius={0.8}
              padAngle={2}
              cornerRadius={0}
              activeOuterRadiusOffset={8}
              enableArcLabels={false}
              enableArcLinkLabels={false}
              theme={{
                legends: {
                  text: {
                    fontFamily: "Inter",
                    fontSize: 12,
                    lineHeight: 14,
                    fontWeight: 500,
                  },
                },
              }}
              layers={[
                "arcLinkLabels",
                "arcs",
                "arcLabels",
                "legends",
                (props) => (
                  <CenteredMetric
                    {...props}
                    totalCosts={costsChart.totalCosts}
                  />
                ),
              ]}
              tooltip={CustomTooltip}
            />
          </div>
          <Legend t={t} chartConfig={chartConfig} costsChart={costsChart} />
        </div>
      </InvestmentGraphic>
      {_renderInfoPopover({ t, info: costsChart.info })}
    </div>
  );
};

export default CostChart;
